import type { InterviewLoaded } from "@repo/app-types/entities/interview";
import type { FC } from "react";
import { DialogTrigger, ToggleButtonGroup } from "react-aria-components";
import { v4 as uuidv4 } from "uuid";
import { useAppDispatch } from "../../../hooks/hook.js";
import { updateInterview } from "../../interviews/interviewsSlice.js";
import {
  NotificationType,
  showNotification,
} from "../../notificationsOverlay/notificationsSlice.js";
import type { AssessmentFeedbackFormValues } from "./form.js";
import {
  checkIsSentiment,
  trackAssessmentFeedback,
  type AssessmentFeedbackSentiment,
} from "./helpers.js";
import { AssessmentFeedbackSentimentPopover } from "./sentimentPopover.js";
import { AssessmentFeedbackSentimentToggleButton } from "./sentimentToggleButton.js";

interface AssessmentFeedbackWidgetProps {
  interview: InterviewLoaded;
}

/** Shows a pair of thumbs up/down buttons to solicit feedback on the assessment. */
export const AssessmentFeedbackWidget: FC<AssessmentFeedbackWidgetProps> = (
  props,
) => {
  const { interview } = props;
  const currentScore = interview.insights?.assessment_score ?? undefined;

  const assessmentFeedback = interview.metadata?.assessment_feedback;
  const currentSentiment: AssessmentFeedbackSentiment | undefined =
    assessmentFeedback?.sentiment ?? undefined;
  const currentValues: AssessmentFeedbackFormValues = {
    suggestedScore: assessmentFeedback?.suggested_score ?? undefined,
    comments: assessmentFeedback?.comments ?? "",
  };

  const dispatch = useAppDispatch();

  // When the user clicks on a sentiment button.
  const handleSentimentChange = (sentiment: AssessmentFeedbackSentiment) => {
    // Don't do anything if the sentiment is unchanged.
    if (currentSentiment === sentiment) {
      return;
    }

    // Update the interview metadata.
    dispatch(
      updateInterview({
        projectId: interview.project_id,
        interviewId: interview.id,
        update: {
          metadata: {
            assessment_feedback: {
              sentiment,
              // Clear the suggested score and comments when changing sentiment.
              suggested_score: null,
              comments: null,
            },
          },
        },
      }),
    );

    // Track the interaction in PostHog.
    trackAssessmentFeedback({
      projectId: interview.project_id,
      interviewId: interview.id,
      sentiment,
    });
  };

  // When the user submits feedback through the popover.
  const handleFeedbackSubmit = (
    sentiment: AssessmentFeedbackSentiment,
    values: AssessmentFeedbackFormValues,
  ) => {
    // Update the interview metadata.
    dispatch(
      updateInterview({
        projectId: interview.project_id,
        interviewId: interview.id,
        update: {
          metadata: {
            assessment_feedback: {
              sentiment,
              suggested_score: values.suggestedScore ?? null,
              comments: values.comments,
            },
          },
        },
      }),
    );

    // Track the interaction in PostHog.
    trackAssessmentFeedback({
      projectId: interview.project_id,
      interviewId: interview.id,
      originalScore: currentScore,
      suggestedScore: values.suggestedScore,
      comments: values.comments,
      sentiment,
    });

    showNotification(dispatch, {
      id: uuidv4(),
      primaryMessage: "Thank you for your feedback!",
      type: NotificationType.SUCCESS,
    });
  };

  // Don't show the widget until there is an assessment score.
  if (currentScore === undefined) {
    return null;
  }

  return (
    <ToggleButtonGroup
      className="flex space-x-1"
      selectionMode="single"
      selectedKeys={currentSentiment ? [currentSentiment] : []}
      onSelectionChange={(keys) => {
        const key = [...keys].at(0);
        if (checkIsSentiment(key)) {
          handleSentimentChange(key);
        }
      }}
    >
      <DialogTrigger>
        <AssessmentFeedbackSentimentToggleButton sentiment="positive" />
        <AssessmentFeedbackSentimentPopover
          initialValues={
            currentSentiment === "positive" ? currentValues : undefined
          }
          onSubmit={(values) => handleFeedbackSubmit("positive", values)}
        />
      </DialogTrigger>

      <DialogTrigger>
        <AssessmentFeedbackSentimentToggleButton sentiment="negative" />
        <AssessmentFeedbackSentimentPopover
          shouldAskForScore
          initialValues={
            currentSentiment === "negative" ? currentValues : undefined
          }
          onSubmit={(values) => handleFeedbackSubmit("negative", values)}
        />
      </DialogTrigger>
    </ToggleButtonGroup>
  );
};
